import { useLocalStorageContext } from '@src/contexts';
import { useAppContext } from '@src/contexts';
import { useSessionContext } from '@src/modules/main-app/authentication';
import { FC, Fragment, useEffect, useState } from 'react';
import { AndroidInstallDrawer } from './android-install-drawer';
import { LocalStoragePwaKey, PWA_DISMISS_DAYS } from './constants';
import { IOSInstallDrawer } from './ios-install-drawer';
import { usePwaInstall } from './use-pwa-install.hook';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

interface PwaInstallPromptProps {
  /** Espera antes de abrir o convite, para não atropelar o primeiro desenho da tela. */
  delayMs?: number;
}

/**
 * Convida quem usa no celular a instalar o app na tela inicial.
 *
 * Aparece a cada acesso autenticado. Duas condições o protegem de virar
 * incômodo: nunca para quem já instalou, e uma recusa o cala por um prazo --
 * ou seja, insiste no máximo uma vez por período, não a cada visita.
 */
const PwaInstallPrompt: FC<PwaInstallPromptProps> = ({ delayMs = 5000 }) => {
  const { isInstallable, isStandalone, canPrompt, promptInstall } = usePwaInstall();
  const { isAndroid, isIOS } = useAppContext();
  const { userLogged } = useSessionContext();
  const { getLocalStorage, saveLocalStorage } = useLocalStorageContext();

  const [showAndroid, setShowAndroid] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  const numberFrom = (key: string): number => Number(getLocalStorage(key)) || 0;

  useEffect(() => {
    if (userLogged === false || isStandalone) return;

    // Comparado com o relógio, e não só testado como presente: uma recusa
    // silencia o convite por um prazo, não para sempre.
    if (Date.now() < numberFrom(LocalStoragePwaKey.DISMISSED_UNTIL)) return;

    const timer = setTimeout(() => {
      if (isAndroid && isInstallable && canPrompt) {
        setShowAndroid(true);
        return;
      }

      if (isIOS) setShowIOS(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [userLogged, isStandalone, isAndroid, isIOS, isInstallable, canPrompt, delayMs]);

  const handleClose = () => {
    saveLocalStorage(LocalStoragePwaKey.DISMISSED_UNTIL, Date.now() + PWA_DISMISS_DAYS * DAY_IN_MS);

    setShowAndroid(false);
    setShowIOS(false);
  };

  return (
    <Fragment>
      <AndroidInstallDrawer open={showAndroid} onClose={handleClose} onInstall={promptInstall} />
      <IOSInstallDrawer open={showIOS} onClose={handleClose} />
    </Fragment>
  );
};

export { PwaInstallPrompt };
